import React, { useEffect, useState } from "react";
import { HiOutlineUserGroup, HiOutlinePlus, HiOutlineTrash, HiOutlineEye, HiOutlinePencil, HiOutlineCheck, HiOutlineX, HiOutlinePhone, HiOutlineMail, HiOutlineGlobe, HiOutlineLocationMarker, HiOutlineOfficeBuilding, HiOutlineDocumentText, HiOutlineClipboardList } from 'react-icons/hi';

const emptyPartner = {
  code: "",
  name: "",
  contact_person: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  region: "",
  tax_number: "",
  trade_registry: "",
  website: "",
  sector: "",
  status: "active",
  notes: ""
};

export default function PartnerlerPage() {
  const [partners, setPartners] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [newPartner, setNewPartner] = useState(emptyPartner);
  const [editPartner, setEditPartner] = useState(null);
  const [detailPartner, setDetailPartner] = useState(null);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Partnerleri backend'den çek
  useEffect(() => {
    fetch('/api/partners')
      .then(res => res.json())
      .then(data => setPartners(data))
      .catch(() => setPartners([]));
  }, []);

  // Partner ekle
  const handleAdd = async () => {
    if (!newPartner.code || !newPartner.name) return;
    try {
      const resp = await fetch('/api/partners', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPartner)
      });
      if (!resp.ok) throw new Error('Partner eklenemedi');
      setNewPartner(emptyPartner);
      setShowAdd(false);
      // Listeyi güncelle
      fetch('/api/partners')
        .then(res => res.json())
        .then(data => setPartners(data));
    } catch (err) {
      alert('Partner eklenemedi: ' + err.message);
    }
  };

  // Partner güncelle
  const handleEdit = async () => {
    if (!editPartner.code || !editPartner.name) return;
    try {
      const resp = await fetch(`/api/partners/${editPartner.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editPartner)
      });
      if (!resp.ok) throw new Error('Partner güncellenemedi');
      setEditPartner(null);
      setShowEdit(false);
      // Listeyi güncelle
      fetch('/api/partners')
        .then(res => res.json())
        .then(data => setPartners(data));
    } catch (err) {
      alert('Partner güncellenemedi: ' + err.message);
    }
  };

  // Partner sil
  const handleDelete = async (id) => {
    if (!window.confirm('Bu partneri silmek istediğinize emin misiniz?')) return;
    try {
      const resp = await fetch(`/api/partners/${id}`, { method: 'DELETE' });
      if (!resp.ok) throw new Error('Partner silinemedi');
      // Listeyi güncelle
      fetch('/api/partners')
        .then(res => res.json())
        .then(data => setPartners(data));
    } catch (err) {
      alert('Partner silinemedi: ' + err.message);
    }
  };

  // Seçili partnerleri sil
  const handleBulkDelete = async () => {
    if (selectedPartners.length === 0) return;
    if (!window.confirm(`${selectedPartners.length} partneri silmek istediğinize emin misiniz?`)) return;
    try {
      const resp = await fetch('/api/partners/bulk', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedPartners })
      });
      if (!resp.ok) throw new Error('Partnerler silinemedi');
      const result = await resp.json();
      alert(result.message);
      setSelectedPartners([]);
      setSelectAll(false);
      // Listeyi güncelle
      fetch('/api/partners')
        .then(res => res.json())
        .then(data => setPartners(data));
    } catch (err) {
      alert('Partnerler silinemedi: ' + err.message);
    }
  };

  // Tek partner seç/kaldır
  const handleSelectPartner = (id) => {
    setSelectedPartners(prev => 
      prev.includes(id) 
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  // Tümünü seç/kaldır
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPartners([]);
      setSelectAll(false);
    } else {
      setSelectedPartners(filteredPartners.map(p => p.id));
      setSelectAll(true);
    }
  };

  // Detay modalı aç
  const handleShowDetail = (partner) => {
    setDetailPartner(partner);
    setShowDetail(true);
  };

  // Düzenleme modalı aç
  const handleShowEdit = (partner) => {
    setEditPartner({ ...partner });
    setShowEdit(true);
  };

  // Filtreleme
  const filteredPartners = partners.filter(partner => {
    const matchesSearch = partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partner.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partner.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         partner.city?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || partner.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'inactive': return 'Pasif';
      case 'pending': return 'Onay Bekliyor';
      default: return 'Bilinmiyor';
    }
  };

  return (
    <div className="w-full p-6">
      <h1 className="text-3xl font-bold text-blue-900 mb-8 flex items-center gap-3 px-6 pt-6">
        <HiOutlineUserGroup className="w-8 h-8 text-blue-700" /> Partnerler
      </h1>
      <div className="bg-white rounded-2xl shadow-xl p-8 flex flex-col gap-6 border border-blue-100 mt-0">
        <div className="flex justify-between items-center mb-4">
          <span className="font-semibold text-blue-800 text-lg">Bayi Listesi</span>
          <div className="flex gap-2 items-center">
            {selectedPartners.length > 0 && (
              <button
                className="px-4 py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 transition text-sm flex items-center gap-2"
                onClick={handleBulkDelete}
              >
                <HiOutlineTrash className="w-5 h-5" /> Seçili Partnerleri Sil ({selectedPartners.length})
              </button>
            )}
            <button
              className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition text-sm flex items-center gap-2"
              onClick={() => setShowAdd(v => !v)}
            >
              <HiOutlinePlus className="w-5 h-5" /> Partner Ekle
            </button>
          </div>
        </div>

        {/* Filtreler */}
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex flex-col flex-1">
            <label className="text-sm font-semibold text-blue-900 mb-1">Partner Ara</label>
            <input
              type="text"
              placeholder="Partner adı, kodu veya yetkili kişi..."
              className="px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-semibold text-blue-900 mb-1">Durum Filtresi</label>
            <select
              className="px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-200 text-blue-900"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
              <option value="pending">Onay Bekliyor</option>
            </select>
          </div>
        </div>

        {/* Partner Ekleme Formu */}
        {showAdd && (
          <div className="bg-blue-50 rounded-xl p-6 mb-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-4">Yeni Partner Ekle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Partner Kodu</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.code} onChange={e => setNewPartner(p => ({ ...p, code: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Firma Adı</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.name} onChange={e => setNewPartner(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Yetkili Kişi</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.contact_person} onChange={e => setNewPartner(p => ({ ...p, contact_person: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">E-posta</label>
                <input type="email" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.email} onChange={e => setNewPartner(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Telefon</label>
                <input type="tel" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.phone} onChange={e => setNewPartner(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Şehir</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.city} onChange={e => setNewPartner(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Bölge</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.region} onChange={e => setNewPartner(p => ({ ...p, region: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Vergi No</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.tax_number} onChange={e => setNewPartner(p => ({ ...p, tax_number: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Ticaret Sicil No</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.trade_registry} onChange={e => setNewPartner(p => ({ ...p, trade_registry: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Website</label>
                <input type="url" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.website} onChange={e => setNewPartner(p => ({ ...p, website: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Sektör</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.sector} onChange={e => setNewPartner(p => ({ ...p, sector: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Durum</label>
                <select className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={newPartner.status} onChange={e => setNewPartner(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                  <option value="pending">Onay Bekliyor</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-blue-900 mb-1 block">Adres</label>
              <textarea className="w-full px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" rows="2" value={newPartner.address} onChange={e => setNewPartner(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-blue-900 mb-1 block">Notlar</label>
              <textarea className="w-full px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" rows="3" value={newPartner.notes} onChange={e => setNewPartner(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2 mt-4">
              <button className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition" onClick={handleAdd}>Kaydet</button>
              <button className="px-4 py-2 rounded bg-gray-200 text-blue-900 font-semibold hover:bg-gray-300 transition" onClick={() => setShowAdd(false)}>İptal</button>
            </div>
          </div>
        )}

        {/* Partner Listesi */}
        <div className="overflow-x-auto rounded-xl border border-blue-100">
          <table className="min-w-full text-sm">
            <thead className="bg-blue-50">
              <tr className="text-blue-900">
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                </th>
                <th className="px-3 py-2 text-left">Partner</th>
                <th className="px-3 py-2 text-left">Kod</th>
                <th className="px-3 py-2 text-left">Yetkili</th>
                <th className="px-3 py-2 text-left">İletişim</th>
                <th className="px-3 py-2 text-left">Şehir</th>
                <th className="px-3 py-2 text-left">Durum</th>
                <th className="px-3 py-2 text-left">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.length === 0 && (
                <tr><td colSpan={8} className="text-center text-gray-400 py-6">Hiç partner bulunamadı.</td></tr>
              )}
              {filteredPartners.map(partner => (
                <tr key={partner.id} className="hover:bg-blue-50 transition">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selectedPartners.includes(partner.id)}
                      onChange={() => handleSelectPartner(partner.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-3">
                                          <span className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-lg font-bold">
                      <HiOutlineOfficeBuilding className="w-5 h-5" />
                    </span>
                      <div>
                        <div className="font-bold text-blue-900">{partner.name}</div>
                        <div className="text-xs text-gray-500">{partner.sector}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono">{partner.code}</td>
                  <td className="px-3 py-2">{partner.contact_person}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1 text-xs">
                      {partner.email && (
                        <div className="flex items-center gap-1">
                          <HiOutlineMail className="w-3 h-3 text-blue-400" />
                          {partner.email}
                        </div>
                      )}
                      {partner.phone && (
                        <div className="flex items-center gap-1">
                          <HiOutlinePhone className="w-3 h-3 text-blue-400" />
                          {partner.phone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <HiOutlineLocationMarker className="w-4 h-4 text-blue-400" />
                      {partner.city}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(partner.status)}`}>
                      {getStatusText(partner.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 flex gap-2">
                    <button className="px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 text-xs font-semibold flex items-center gap-1" onClick={() => handleShowDetail(partner)}>
                      <HiOutlineEye className="w-4 h-4" /> Detay
                    </button>
                    <button className="px-2 py-1 rounded bg-yellow-100 text-yellow-700 hover:bg-yellow-200 text-xs font-semibold flex items-center gap-1" onClick={() => handleShowEdit(partner)}>
                      <HiOutlinePencil className="w-4 h-4" /> Düzenle
                    </button>
                    <button className="px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 text-xs font-semibold flex items-center gap-1" onClick={() => handleDelete(partner.id)}>
                      <HiOutlineTrash className="w-4 h-4" /> Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detay Modalı */}
      {showDetail && detailPartner && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl flex flex-col gap-4 border border-blue-100 animate-fade-in">
            <div className="text-2xl font-bold text-blue-900 text-center mb-2 flex items-center gap-2">
              <HiOutlineOfficeBuilding className="w-7 h-7 text-blue-700" /> Partner Detayı
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div><b>Partner Kodu:</b> {detailPartner.code}</div>
              <div><b>Firma Adı:</b> {detailPartner.name}</div>
              <div><b>Yetkili Kişi:</b> {detailPartner.contact_person}</div>
              <div><b>E-posta:</b> {detailPartner.email}</div>
              <div><b>Telefon:</b> {detailPartner.phone}</div>
              <div><b>Şehir:</b> {detailPartner.city}</div>
              <div><b>Bölge:</b> {detailPartner.region}</div>
              <div><b>Vergi No:</b> {detailPartner.tax_number}</div>
              <div><b>Ticaret Sicil No:</b> {detailPartner.trade_registry}</div>
              <div><b>Website:</b> {detailPartner.website ? <a href={detailPartner.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{detailPartner.website}</a> : '-'}</div>
              <div><b>Sektör:</b> {detailPartner.sector}</div>
              <div><b>Durum:</b> <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(detailPartner.status)}`}>{getStatusText(detailPartner.status)}</span></div>
              <div><b>Adres:</b> {detailPartner.address}</div>
              <div><b>Notlar:</b> {detailPartner.notes}</div>
              <div><b>Oluşturulma Tarihi:</b> {new Date(detailPartner.created_at).toLocaleDateString('tr-TR')}</div>
              <div><b>Güncellenme Tarihi:</b> {new Date(detailPartner.updated_at).toLocaleDateString('tr-TR')}</div>
            </div>
            <button className="mt-4 px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition" onClick={() => setShowDetail(false)}>Kapat</button>
          </div>
        </div>
      )}

      {/* Düzenleme Modalı */}
      {showEdit && editPartner && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-4xl flex flex-col gap-4 border border-blue-100 animate-fade-in">
            <div className="text-2xl font-bold text-blue-900 text-center mb-2 flex items-center gap-2">
              <HiOutlinePencil className="w-7 h-7 text-blue-700" /> Partner Düzenle
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Partner Kodu</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.code} onChange={e => setEditPartner(p => ({ ...p, code: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Firma Adı</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.name} onChange={e => setEditPartner(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Yetkili Kişi</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.contact_person} onChange={e => setEditPartner(p => ({ ...p, contact_person: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">E-posta</label>
                <input type="email" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.email} onChange={e => setEditPartner(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Telefon</label>
                <input type="tel" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.phone} onChange={e => setEditPartner(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Şehir</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.city} onChange={e => setEditPartner(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Bölge</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.region} onChange={e => setEditPartner(p => ({ ...p, region: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Vergi No</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.tax_number} onChange={e => setEditPartner(p => ({ ...p, tax_number: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Ticaret Sicil No</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.trade_registry} onChange={e => setEditPartner(p => ({ ...p, trade_registry: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Website</label>
                <input type="url" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.website} onChange={e => setEditPartner(p => ({ ...p, website: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Sektör</label>
                <input type="text" className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.sector} onChange={e => setEditPartner(p => ({ ...p, sector: e.target.value }))} />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-semibold text-blue-900 mb-1">Durum</label>
                <select className="px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" value={editPartner.status} onChange={e => setEditPartner(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif</option>
                  <option value="pending">Onay Bekliyor</option>
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-blue-900 mb-1 block">Adres</label>
              <textarea className="w-full px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" rows="2" value={editPartner.address} onChange={e => setEditPartner(p => ({ ...p, address: e.target.value }))} />
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-blue-900 mb-1 block">Notlar</label>
              <textarea className="w-full px-3 py-2 rounded border border-blue-200 focus:ring-2 focus:ring-blue-200 text-blue-900" rows="3" value={editPartner.notes} onChange={e => setEditPartner(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="flex gap-2 mt-4">
              <button className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition flex items-center gap-2" onClick={handleEdit}>
                <HiOutlineCheck className="w-5 h-5" /> Kaydet
              </button>
              <button className="px-4 py-2 rounded bg-gray-200 text-blue-900 font-semibold hover:bg-gray-300 transition flex items-center gap-2" onClick={() => setShowEdit(false)}>
                <HiOutlineX className="w-5 h-5" /> İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 